import React from "react";
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import { ContentPasteSearchOutlined } from "@mui/icons-material";

export const LanguageSelection = ({
  isLoading,
  languageList,
  selectedLanguage,
  setSelectedLanguage,
  languages,
  setLanguages,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");

  const filteredLanguages = languageList.filter((item) =>
    item.languagename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if a language is selected by its ID
  const isLanguageChecked = (languageId) => languages.includes(languageId);

  // Handle toggling the selection of a language by its ID
  const handleToggleLanguage = (languageId) => {
    setLanguages((prevLanguages) => {
      const exists = prevLanguages.includes(languageId);

      if (exists) {
        // Remove the language if already selected
        const updatedLanguages = prevLanguages.filter(
          (id) => id !== languageId
        );
        if (selectedLanguage?.id === languageId) {
          setSelectedLanguage(null); // Clear selected language
        }
        return updatedLanguages;
      } else {
        // Add the new language ID
        return [...prevLanguages, languageId];
      }
    });
  };

  return (
    <Box
      flex={1}
      borderRadius="12px"
      px="8px"
      py={3}
      bgcolor="#F7F7F7"
      display="flex"
      flexDirection="column"
    >
      <Stack
        pb={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {isLoading ? (
          <Skeleton variant="rounded" width={140} height={24} />
        ) : (
          <Typography fontWeight={400} fontSize={18} color="#222222">
            Languages
          </Typography>
        )}
      </Stack>

      <Box>
        {isLoading ? (
          <Skeleton variant="rounded" width={180} height={18} />
        ) : (
          <Typography color="textDisabled">
            Choose languages that you want to assign to this lead
          </Typography>
        )}

        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <TextField
            id="outlined-start-adornment"
            placeholder="Search language"
            fullWidth
            className="inputMdRounded"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ bgcolor: "transparent" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <ContentPasteSearchOutlined />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
      </Box>

      <List dense sx={{ width: "100%", flex: 1, overflowY: "auto" }}>
        {isLoading
          ? Array.from({ length: 7 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                sx={{ mt: 1 }}
                width={"100%"}
                height={32}
              />
            ))
          : filteredLanguages.map((item) => (
              <ListItem
                key={item.id}
                secondaryAction={
                  <Checkbox
                    edge="end"
                    onChange={() => handleToggleLanguage(item.id)}
                    checked={isLanguageChecked(item.id)}
                    sx={{
                      color: selectedLanguage?.id === item.id ? "white" : "gray",
                      "&.Mui-checked": {
                        color:
                          selectedLanguage?.id === item.id ? "white" : "#3A80F7",
                      },
                      display:
                        isLanguageChecked(item.id) || selectedLanguage?.id === item.id
                          ? "inline-flex"
                          : "none",
                    }}
                  />
                }
                disablePadding
                sx={{
                  bgcolor:
                    selectedLanguage?.id === item.id
                      ? "#3A80F7"
                      : isLanguageChecked(item.id)
                      ? "#E4EBF4"
                      : "transparent",
                  "&:hover": {
                    "& .MuiCheckbox-root": {
                      display: "inline-flex",
                    },
                  },
                }}
              >
                <ListItemButton
                  onClick={() => {
                    setSelectedLanguage(item);
                    handleToggleLanguage(item.id);
                  }}
                >
                  <ListItemText
                    sx={{
                      color: selectedLanguage?.id === item.id ? "white" : "black",
                    }}
                    primary={item.languagename}
                  />
                </ListItemButton>
              </ListItem>
            ))}
      </List>
    </Box>
  );
};
